import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "dark" | "light" | "system";
/** Global overlays opened by shortcuts: quick switcher, command palette, shortcut reference, status. */
export type Overlay = "switcher" | "palette" | "shortcuts" | "status" | null;
/** A community message being shared to the public feed. */
export interface ComposerShare {
  messageId: string;
  content: string;
  author: { displayName: string; avatarUrl: string | null };
  channelName: string;
  communityName: string;
}

export type FeedMode = "for_you" | "following" | "latest" | "trending" | "communities";

interface UIState {
  /** Last community route, so returning to Communities restores where you were. */
  lastCommunityPath: string;
  setLastCommunityPath: (p: string) => void;
  /** Last conversation and Explore tab, restored the same way. */
  lastMessagesPath: string;
  lastExplorePath: string;
  setLastPath: (section: "messages" | "explore", path: string) => void;
  overlay: Overlay;
  setOverlay: (o: Overlay) => void;
  /** Last channel per community. */
  lastChannel: Record<string, string>;
  setLastChannel: (communityId: string, channelId: string) => void;
  feedMode: FeedMode;
  setFeedMode: (m: FeedMode) => void;
  membersOpen: boolean;
  toggleMembers: () => void;
  /** Text chat beside voice and stage rooms (desktop). Off by default so the room has the space. */
  voiceChatOpen: boolean;
  setVoiceChatOpen: (open: boolean) => void;
  composerOpen: boolean;
  composerQuoteId: string | null;
  composerShare: ComposerShare | null;
  composerCommunity: { id: string; name: string } | null;
  openComposer: (opts?: { quoteId?: string; share?: ComposerShare; community?: { id: string; name: string } }) => void;
  closeComposer: () => void;
  createCommunityOpen: boolean;
  setCreateCommunityOpen: (v: boolean) => void;
  /** Community invite shown in the invite popup. */
  inviteCode: string | null;
  openInvite: (code: string | null) => void;
}

export const useUI = create<UIState>()(
  persist(
    (set) => ({
      lastCommunityPath: "/c",
      setLastCommunityPath: (lastCommunityPath) => set({ lastCommunityPath }),
      lastMessagesPath: "/messages",
      lastExplorePath: "/explore",
      setLastPath: (section, path) => set(section === "messages" ? { lastMessagesPath: path } : { lastExplorePath: path }),
      overlay: null,
      setOverlay: (overlay) => set({ overlay }),
      lastChannel: {},
      setLastChannel: (communityId, channelId) => set((s) => ({ lastChannel: { ...s.lastChannel, [communityId]: channelId } })),
      feedMode: "for_you",
      setFeedMode: (feedMode) => {
        markFeedPicked();
        set({ feedMode });
      },
      membersOpen: true,
      toggleMembers: () => set((s) => ({ membersOpen: !s.membersOpen })),
      voiceChatOpen: false,
      setVoiceChatOpen: (voiceChatOpen) => set({ voiceChatOpen }),
      composerOpen: false,
      composerQuoteId: null,
      composerShare: null,
      composerCommunity: null,
      openComposer: (opts) =>
        set({ composerOpen: true, composerQuoteId: opts?.quoteId ?? null, composerShare: opts?.share ?? null, composerCommunity: opts?.community ?? null }),
      closeComposer: () => set({ composerOpen: false, composerQuoteId: null, composerShare: null, composerCommunity: null }),
      createCommunityOpen: false,
      setCreateCommunityOpen: (createCommunityOpen) => set({ createCommunityOpen }),
      inviteCode: null,
      openInvite: (inviteCode) => set({ inviteCode }),
    }),
    {
      name: "atrium:ui",
      partialize: (s) => ({
        lastChannel: s.lastChannel,
        feedMode: s.feedMode,
        membersOpen: s.membersOpen,
        voiceChatOpen: s.voiceChatOpen,
        lastCommunityPath: s.lastCommunityPath,
        lastMessagesPath: s.lastMessagesPath,
        lastExplorePath: s.lastExplorePath,
      }),
    },
  ),
);

/* The account's default feed applies until the user picks a mode in this session (tab). */
const FEED_PICKED_KEY = "atrium:feed-picked";
function markFeedPicked() {
  try {
    sessionStorage.setItem(FEED_PICKED_KEY, "1");
  } catch {
    /* ignore */
  }
}
function feedPicked() {
  try {
    return sessionStorage.getItem(FEED_PICKED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Use the preferred default feed unless a mode was chosen this session. */
export function applyDefaultFeed(mode: FeedMode) {
  if (feedPicked()) return;
  if (useUI.getState().feedMode !== mode) useUI.setState({ feedMode: mode });
}

/*
 * The theme is a synced preference now (prefs.theme, applied by lib/prefs.ts).
 * A copy in localStorage lets index.html and main.tsx paint the right theme
 * before /api/me loads.
 */
